/**
 * Client-side mirror of the backend issue taxonomy
 * (`server/fastlane/traveler_audit/issue_categories.py`) plus a lightweight
 * template normalizer, so the issues modal can clump near-identical
 * failure/lookout strings under one expandable header instead of listing dozens
 * of balloon-by-balloon lines.
 *
 * Keep the category order and patterns in sync with the Python source: matching
 * is substring-based (case-insensitive), first match wins.
 */

interface CategoryDef {
	key: string;
	label: string;
	patterns: string[];
}

/** Ordered most-specific first; mirrors ISSUE_CATEGORIES in Python. */
export const ISSUE_CATEGORIES: CategoryDef[] = [
	{ key: 'tolerance', label: 'Out of tolerance', patterns: ['out of tolerance'] },
	{ key: 'near_limit', label: 'Near tolerance limit', patterns: ['of a tolerance limit'] },
	{ key: 'oot_tool', label: 'Out-of-calibration tool', patterns: ['out-of-calibration gage'] },
	{ key: 'missing_gage', label: 'Missing gage ID', patterns: ['no gage recorded'] },
	{ key: 'cal_cert', label: 'Missing calibration cert', patterns: ['no traceable'] },
	{ key: 'cal_due_soon', label: 'Calibration due soon', patterns: ['calibration due'] },
	{
		key: 'material_cert',
		label: 'Missing material cert',
		patterns: ['material certificate is missing'],
	},
	{ key: 'missing_fai', label: 'Missing FAI record', patterns: ['fai record is missing'] },
	{ key: 'unmeasured', label: 'Unmeasured balloon', patterns: ['no recorded measurement'] },
	{ key: 'sample_count', label: 'Too few samples', patterns: ['fewer recorded samples'] },
	{
		key: 'aql_overproduction',
		label: 'AQL changed by overproduction',
		patterns: ['changes aql sample size'],
	},
	{ key: 'visual', label: 'Visual not pass', patterns: ['visual result is not pass'] },
	{
		key: 'sequence',
		label: 'Date sequence',
		patterns: [
			'dated before the preceding step',
			'is after',
			'dated before',
			'must be completed before',
			'greatest of the top 4',
			'intake date',
		],
	},
	{
		key: 'signoff',
		label: 'Missing sign-off',
		patterns: [
			'no operator/sign-off',
			'no operator attributed',
			'has an operator but no date',
		],
	},
	{
		key: 'revision',
		label: 'Revision',
		patterns: ['does not match the revision', 'revision is missing'],
	},
	{ key: 'inspector', label: 'Missing inspector', patterns: ['missing inspector/date'] },
	{
		key: 'workflow_initials',
		label: 'Missing workflow initials',
		patterns: ['missing initials/date'],
	},
	{ key: 'quantity', label: 'Quantity mismatch', patterns: ['outake sum', 'intake sum'] },
	{
		key: 'required_field',
		label: 'Missing required field',
		patterns: ['required field', 'table is empty'],
	},
	{
		key: 'intake_operator',
		label: 'Intake operator mismatch',
		patterns: ['does not match expected'],
	},
	{
		key: 'initials_collision',
		label: 'Initials collision',
		patterns: ['collides with another operator'],
	},
];

const OTHER_KEY = 'other';
const OTHER_LABEL = 'Other';

export const CATEGORY_LABELS: Record<string, string> = {
	...Object.fromEntries(ISSUE_CATEGORIES.map((c) => [c.key, c.label])),
	[OTHER_KEY]: OTHER_LABEL,
};

/** Return the category key for a single failure/lookout string. */
export function categorizeIssue(text: string): string {
	const low = (text || '').toLowerCase();
	for (const cat of ISSUE_CATEGORIES) {
		if (cat.patterns.some((p) => low.includes(p))) return cat.key;
	}
	return OTHER_KEY;
}

/**
 * Collapse volatile tokens (numbers, ranges, gage serials, dates) so that
 * messages differing only in their specifics cluster together. The output is a
 * stable key, not display text.
 */
export function normalizeIssueTemplate(text: string): string {
	return (text || '')
		.toLowerCase()
		.replace(/\[[^\]]*\]/g, '[…]') // bracketed tolerance ranges
		.replace(/\(([^)]*)\)/g, (m) => (/\d/.test(m) ? '(…)' : m)) // numeric parens only
		.replace(/\b[a-z]{1,4}-\d+[a-z0-9-]*/g, '#') // gage serials like C-001
		.replace(/\d{4}-\d{2}-\d{2}/g, '#date') // ISO dates
		.replace(/[-+]?\d[\d,.]*/g, '#') // any remaining numbers
		.replace(/\s+/g, ' ')
		.trim();
}

export interface IssueCluster {
	/** A representative message for the cluster (first occurrence). */
	sample: string;
	/** Every raw message that normalized to this cluster, in original order. */
	items: string[];
}

export interface GroupedCategory {
	key: string;
	label: string;
	/** Total raw messages in this category. */
	count: number;
	clusters: IssueCluster[];
}

/**
 * Two-level grouping: by taxonomy category, then by normalized template within
 * each category. Categories are ordered by descending count (ties broken by
 * label) so the noisiest buckets surface first.
 */
export function groupIssues(items: string[]): GroupedCategory[] {
	const byCategory = new Map<string, Map<string, IssueCluster>>();

	for (const raw of items) {
		const catKey = categorizeIssue(raw);
		const tmpl = normalizeIssueTemplate(raw);
		let clusters = byCategory.get(catKey);
		if (!clusters) {
			clusters = new Map<string, IssueCluster>();
			byCategory.set(catKey, clusters);
		}
		const cluster = clusters.get(tmpl);
		if (cluster) {
			cluster.items.push(raw);
		} else {
			clusters.set(tmpl, { sample: raw, items: [raw] });
		}
	}

	const groups: GroupedCategory[] = [];
	for (const [key, clusters] of byCategory.entries()) {
		const clusterList = Array.from(clusters.values()).sort(
			(a, b) => b.items.length - a.items.length
		);
		const count = clusterList.reduce((sum, c) => sum + c.items.length, 0);
		groups.push({
			key,
			label: CATEGORY_LABELS[key] ?? key,
			count,
			clusters: clusterList,
		});
	}

	groups.sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
	return groups;
}
